import React, { useState, useEffect } from 'react';
import { ArrowLeft, Upload, Plus, X, Clock, Package, Tag, Euro, Trash2, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import { useImageStorage } from '@/hooks/useImageStorage';
import BottomNavigation from '@/components/BottomNavigation';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
}

// Mock data for editing - in real app this would come from API/database
const mockProductData = {
  301: {
    id: 301,
    name: "Social Media Mastery E-book",
    description: "Complete gids voor social media marketing met praktische tips en templates.",
    price: "35",
    category: "Digitale Producten",
    type: "digital",
    stock: "",
    tags: ["E-book", "Social Media", "Marketing"],
    images: [] as ImageFile[]
  },
  302: {
    id: 302,
    name: "Instagram Template Pack",
    description: "30 professionele Instagram post templates voor ondernemers.",
    price: "25",
    category: "Digitale Producten", 
    type: "digital",
    stock: "",
    tags: ["Templates", "Instagram", "Design"],
    images: [] as ImageFile[]
  },
  303: {
    id: 303,
    name: "Business Goal Planner",
    description: "Fysieke planner voor het stellen en behalen van business doelen.",
    price: "45",
    category: "Business Tools",
    type: "physical",
    stock: "25",
    tags: ["Planner", "Goals", "Business"],
    images: [] as ImageFile[]
  },
  101: {
    id: 101,
    name: "Marketing Mastery E-book",
    description: "Complette gids voor succesvolle marketing strategieën. 150+ pagina's met praktische tips.",
    price: "29",
    category: "Digitale Producten",
    type: "digital",
    stock: "",
    tags: ["E-book", "Marketing", "PDF"],
    images: [] as ImageFile[]
  },
  102: {
    id: 102,
    name: "Logo Design Pakket",
    description: "Professioneel logo design met 3 concepten, onbeperkte revisies en alle bestandsformaten.",
    price: "150",
    category: "Creative Producten",
    type: "digital",
    stock: "",
    tags: ["Logo", "Branding", "Custom"],
    images: [] as ImageFile[]
  }
};

const productCategories = [
  'Digitale Producten',
  'Creative Producten', 
  'Business Tools',
  'Lifestyle Producten',
  'Beauty & Wellness',
  'Fashion & Accessoires'
];

const EditProduct = () => {
  const navigate = useNavigate();
  const { id: productId } = useParams();

  const { images: storedImages, updateImages, clearImages } = useImageStorage(
    `product-images-edit-${productId}`
  );

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    type: '',
    stock: '',
    tags: [] as string[],
    images: storedImages
  });
  const [newTag, setNewTag] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load existing product data
  useEffect(() => {
    if (productId) {
      setIsLoading(true);

      // Simulate API call - in real app this would be an actual API call
      setTimeout(() => {
        const existingProduct = mockProductData[productId as keyof typeof mockProductData];

        if (existingProduct) {
          setFormData({
            name: existingProduct.name,
            description: existingProduct.description,
            price: existingProduct.price,
            category: existingProduct.category,
            type: existingProduct.type,
            stock: existingProduct.stock,
            tags: existingProduct.tags,
            images: storedImages // Use stored images or existing images
          });

          console.log('Loaded product data for editing:', existingProduct);
          toast.info('Product data geladen voor bewerking');
        } else {
          toast.error('Product niet gevonden');
          navigate('/marketplace');
        }

        setIsLoading(false);
      }, 500); // Simulate network delay
    }
  }, [productId, navigate, storedImages]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImagesChange = (images: ImageFile[]) => {
    setFormData(prev => ({
      ...prev,
      images
    }));
    updateImages(images);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.description || !formData.price || !formData.category) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    setIsSaving(true);

    console.log('Updating product:', {
      ...formData,
      imageCount: formData.images.length,
      imageNames: formData.images.map(img => img.name),
      productId
    });

    // Simulate save
    setTimeout(() => {
      setIsSaving(false);
      toast.success(`Product "${formData.name}" succesvol bijgewerkt!`);

      // Clear stored images after successful submission
      clearImages();

      // Navigate back to product detail or marketplace
      navigate(`/product/${productId}`);
    }, 1000);
  };

  const handleDelete = () => {
    if (window.confirm('Weet je zeker dat je dit product wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.')) {
      toast.success('Product wordt verwijderd...');

      // Simulate delete
      setTimeout(() => {
        clearImages();
        toast.success('Product succesvol verwijderd');
        navigate('/marketplace');
      }, 1000);
    }
  };

  // Update form when stored images change
  useEffect(() => {
    setFormData(prev => ({ ...prev, images: storedImages }));
  }, [storedImages]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-elegant">
        <div className="max-w-2xl mx-auto px-6 py-8">
          <div className="glass-effect border-b border-cream-300 mb-8">
            <div className="flex items-center gap-4 py-4">
              <div className="skeleton h-8 w-8 rounded-full" />
              <div>
                <div className="skeleton-title h-6 w-48 mb-2" />
                <div className="skeleton-text h-4 w-64" />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="card-premium">
                <CardHeader>
                  <div className="skeleton-title h-6 w-32" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="skeleton h-10 w-full" />
                    <div className="skeleton h-20 w-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-elegant pb-24">
      {/* Header */}
      <div className="glass-effect border-b border-cream-300">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="hover:bg-cream-100 text-navy-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-serif text-navy-800">
                Product Bewerken
              </h1>
              <p className="text-sm text-navy-600">
                Pas je product aan en sla de wijzigingen op
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleDelete}
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Verwijderen
              </Button>
            </div>
          </div>

          {/* Product Header */}
          <div className="card-premium p-4 mb-4">
            <div className="text-center">
              <div className="mb-2">
                <h1 className="script-text text-3xl text-navy-800">
                  Product Bewerken
                </h1>
              </div>
              <p className="text-sm text-navy-600">
                Verfijn je product voor de WOM community
              </p>
            </div>
          </div>

          {/* Edit Mode Badge */}
          <div className="mt-3">
            <Badge variant="outline" className="bg-cream-100 text-navy-700 border-cream-300">
              <Clock className="h-3 w-3 mr-1" />
              Bewerken: {formData.name || 'Product'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Card */}
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="text-lg font-serif flex items-center gap-2 text-navy-800">
                <Package className="h-5 w-5 text-navy-600" />
                Basis Informatie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-navy-700">Product Naam *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Bijv. Social Media Templates Pack"
                  className="mt-1 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-navy-700">Beschrijving *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Beschrijf je product, wat maakt het bijzonder?"
                  className="mt-1 h-24 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price" className="text-navy-700">Prijs (€) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder="25.00"
                    className="mt-1 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
                  />
                </div>
                <div>
                  <Label htmlFor="stock" className="text-navy-700">Voorraad</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => handleInputChange('stock', e.target.value)}
                    placeholder="Onbeperkt laat leeg"
                    className="mt-1 border-cream-300 focus:border-gold-400 focus:ring-gold-400"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Category & Type Card */}
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="text-lg font-serif flex items-center gap-2 text-navy-800">
                <Tag className="h-5 w-5 text-navy-600" />
                Categorie & Type
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-navy-700">Categorie *</Label>
                <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                  <SelectTrigger className="mt-1 border-cream-300 bg-white">
                    <SelectValue placeholder="Kies een categorie" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-cream-300">
                    {(productCategories ?? []).map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-navy-700">Product Type</Label>
                <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                  <SelectTrigger className="mt-1 border-cream-300 bg-white">
                    <SelectValue placeholder="Kies product type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-cream-300">
                    <SelectItem value="digital">Digitaal Product</SelectItem>
                    <SelectItem value="physical">Fysiek Product</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Tags Card */}
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="text-lg font-serif text-navy-800">Tags & Categorieën</CardTitle>
              <p className="text-sm text-navy-600">
                Voeg tags toe om je product beter vindbaar te maken
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Voeg een tag toe..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  className="border-cream-300 focus:border-gold-400 focus:ring-gold-400"
                />
                <Button 
                  type="button" 
                  onClick={addTag} 
                  size="sm"
                  className="btn-elegant-primary"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {(formData.tags ?? []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {(formData.tags ?? []).map((tag) => (
                    <Badge key={tag} variant="secondary" className="bg-cream-100 text-navy-700 flex items-center gap-1">
                      {tag}
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Images Card */}
          <Card className="card-premium">
            <CardHeader>
              <CardTitle className="text-lg font-serif text-navy-800">Product Foto's</CardTitle>
              <p className="text-sm text-navy-600">
                Laat je product zien - goede foto's verkopen beter!
              </p>
            </CardHeader>
            <CardContent>
              <ImageUpload
                images={formData.images}
                onImagesChange={handleImagesChange}
                maxImages={5}
                maxSizeInMB={5}
              />
            </CardContent>
          </Card>

          {/* Digital vs Physical Product Info */}
          {formData.type && (
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="text-lg font-serif text-navy-800">
                  {formData.type === 'digital' ? 'Digitaal Product Info' : 'Fysiek Product Info'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {formData.type === 'digital' ? (
                  <div className="bg-cream-50 rounded-lg p-4 border border-cream-200">
                    <div className="flex items-start gap-2">
                      <Package className="h-5 w-5 text-navy-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-navy-800 mb-1">Digitale Levering</h4>
                        <p className="text-sm text-navy-600">
                          Je product wordt automatisch geleverd via email na aankoop. 
                          Zorg ervoor dat je een downloadlink of toegangsinstructies hebt.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-cream-50 rounded-lg p-4 border border-cream-200">
                    <div className="flex items-start gap-2">
                      <Package className="h-5 w-5 text-navy-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-navy-800 mb-1">Fysieke Levering</h4>
                        <p className="text-sm text-navy-600 mb-2">
                          Dit product wordt fysiek verzonden. Zorg ervoor dat je verzendkosten 
                          en levertijden duidelijk communiceert.
                        </p>
                        <p className="text-xs text-navy-500">
                          Voorraad: {formData.stock || 'Onbeperkt'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* WOM Badge */}
          <div className="bg-gold-50 rounded-lg p-4 border border-gold-200">
            <p className="font-medium text-sm flex items-center gap-2 text-navy-800">
              <div className="gradient-gold text-white px-3 py-1 rounded-full text-xs font-bold">
                WOM
              </div>
              Woman on a Mission - Deel je creaties met onze community
            </p>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4 pt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate(-1)}
              className="btn-elegant-secondary flex-1"
              disabled={isSaving}
            >
              Annuleren
            </Button>
            <Button 
              type="submit" 
              className="btn-elegant-primary flex-1"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Opslaan...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Wijzigingen Opslaan
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default EditProduct;